import { AppData, EntschaedigungCalculation, LehrpersonStats } from "../types";

/**
 * Check if a teacher qualifies for Altersentlastung in a given school year.
 * AE applies from the school year in which the LP turns 57 (starting Sept 1).
 * Schuljahr 2025 = 1.9.2025 - 31.8.2026 → LP must turn 57 by 31.8.2026.
 */
export const checkAltersentlastung = (
  geburtsdatum: string | undefined,
  schuljahr: number
): boolean => {
  if (!geburtsdatum) return false;
  const birthDate = new Date(geburtsdatum);
  if (isNaN(birthDate.getTime())) return false;

  // End of school year: 31.8. of the following year
  const endOfSchoolYear = new Date(schuljahr + 1, 7, 31); // Month 7 = August
  const ageAtEnd = endOfSchoolYear.getFullYear() - birthDate.getFullYear();
  const hadBirthdayByEnd =
    endOfSchoolYear.getMonth() > birthDate.getMonth() ||
    (endOfSchoolYear.getMonth() === birthDate.getMonth() &&
      endOfSchoolYear.getDate() >= birthDate.getDate());

  const actualAge = hadBirthdayByEnd ? ageAtEnd : ageAtEnd - 1;
  return actualAge >= 57;
};

/**
 * Calculate weighted basislektionen from a teacher's actual allocations.
 * Falls back to the first subject in the teacher's faecher list.
 */
const getWeightedBasislektionen = (
  lpId: string,
  lpFaecher: string[],
  data: AppData
): { base: number; baseAE: number } => {
  const lpZuteilungen = data.zuteilungen.filter(
    (z) => z.lpId === lpId && z.lpId !== null
  );

  if (lpZuteilungen.length > 0) {
    let totalLekt = 0;
    let weightedBase = 0;
    let weightedBaseAE = 0;

    for (const zt of lpZuteilungen) {
      const fach = data.faecher.find((f) => f.id === zt.fachId);
      const base = fach?.basislektionen ?? 24;
      const baseAE = fach?.basislektionenAltersentlastung ?? base;
      weightedBase += zt.lektionen * base;
      weightedBaseAE += zt.lektionen * baseAE;
      totalLekt += zt.lektionen;
    }

    if (totalLekt > 0) {
      return {
        base: weightedBase / totalLekt,
        baseAE: weightedBaseAE / totalLekt,
      };
    }
  }

  // Fallback: first subject in LP's faecher list
  const firstFach = data.faecher.find(
    (f) => f.kuerzel === lpFaecher[0]
  );
  const base = firstFach?.basislektionen ?? 24;
  const baseAE = firstFach?.basislektionenAltersentlastung ?? base;
  return { base, baseAE };
};

export const calculateLehrpersonStats = (
  lpId: string,
  data: AppData
): LehrpersonStats => {
  const lp = data.lehrpersonen.find((l) => l.id === lpId);
  if (!lp) {
    return {
      soll: 0,
      ist: 0,
      entschaedigung: 0,
      differenz: 0,
      prozent: 0,
      status: "Ausgeglichen",
      hatAltersentlastung: false,
      gewichteteBasislektionen: 24,
    };
  }

  // Get schuljahr from klassen (use the first one, or default to current year)
  const schuljahr =
    data.klassen.length > 0 ? data.klassen[0].schuljahr : new Date().getFullYear();

  // Calculate weighted basislektionen from allocations
  const { base: weightedBase, baseAE: weightedBaseAE } =
    getWeightedBasislektionen(lpId, lp.faecher, data);

  // Check Altersentlastung
  const hatAE = checkAltersentlastung(lp.geburtsdatum, schuljahr);

  // Calculate effective Soll
  let soll = lp.solllektionen;
  if (hatAE && weightedBase > 0) {
    soll = lp.solllektionen * (weightedBaseAE / weightedBase);
  }

  const zuteilungen = data.zuteilungen.filter(
    (z) => z.lpId === lpId && z.lpId !== null
  );
  const ist = zuteilungen.reduce((sum, z) => sum + z.lektionen, 0);
  const entschaedigung = zuteilungen.reduce(
    (sum, z) => sum + z.entschaedigungStunden,
    0
  );
  const differenz = ist - soll;
  const prozent = soll > 0 ? (ist / soll) * 100 : 0;

  let status: LehrpersonStats["status"] = "Ausgeglichen";
  if (differenz < -2) status = "Unterbelastet";
  if (differenz > 2) status = "Überbelastet";

  return {
    soll,
    ist,
    entschaedigung,
    differenz,
    prozent,
    status,
    hatAltersentlastung: hatAE,
    gewichteteBasislektionen: weightedBase,
  };
};

export const calculateEntschaedigung = (
  klasseId: string,
  _typ: string,
  lektionen: number,
  klassen: AppData["klassen"]
): EntschaedigungCalculation => {
  const klasse = klassen.find((k) => k.id === klasseId);
  if (!klasse) return { faktor: 1, stunden: lektionen };

  let faktor = 1.0;

  if (klasse.bestand < 7) {
    faktor = 0.75;
  }

  return {
    faktor: faktor,
    stunden: lektionen * faktor,
  };
};
